import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { useResizeDetector } from 'react-resize-detector';
import { debounce } from 'lodash';

import { getEnvConstant } from 'constants/env';

import BasicInformationRegist from './components/BasicInformationRegist';
import OptionSelectRegist from './components/OptionSelectRegist';
import InformationRegist from './components/InformationRegist';

import FloatingContainer from './components/FloatingContainer';
import { useProductRegistHooks } from './hooks/useProductRegistHooks';
import SelectButton from './components/common/SelectButton';
import Row from 'components/common/Row';
import {
  ICON_CLOSE,
  ICON_CLOSE_WHITE,
  ICON_PRODUCT_GUIDE,
  ICON_PRODUCT_PREVIEW,
  ICON_QUESTION,
} from 'assets/icons';
import ModalContainer from './components/common/ModalContainer';
import PreviewProductDetail from './components/PreviewProductDetail';
import { PRODUCT_CREATE_GUIDE } from 'constants/path';
import RegistCompleteContainer from './components/RegistCompleteContainer';
import Text from 'components/common/Text';
import CommonImage from 'components/common/CommonImage';
import Column from 'components/common/Column';
import HackleManager from 'utils/hackle';
import InspectionComepleteModal from './components/ProductInspection/InspectionCompleteModal';
import InspectionFaildeModal from './components/ProductInspection/InspectionFaildeModal';
import { MAX_WIDTH } from 'App';
import HeaderButton from './components/common/HeaderButton';
import IconClose from 'assets/icons/IconClose';
import { HEADER_HEIGHT } from 'constants/style';
import Loading from 'components/Loading/Loading';

export const EXCEPT_HEIGHT = 157;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  height: 100%;

  min-height: 100vh;
  max-height: calc(100vh - ${HEADER_HEIGHT}px);

  background-color: #f7f7f8;
`;

const ProductRegistPage = () => {
  const {
    registPageNumber,
    productParamInput,
    imageThumbnail,
    isShowCompleteView,
    inquiryList,
    isOpenGuideModal,
    isOpenModal,
    errorMessage,
    isRequestInspectionLoading,
    isSubmittingProduct,
    setIsShowCompleteView,
    setRegistPageNumber,
    setProductParamInput,
    setImageThumbnail,
    handleOnChangeProductParamInput,
    handleProductTemporarySave,
    isVaildCheckBasicPage,
    handleNavigateBackwardPage,
    handleNavigatePage,
    handleRequestInspection,
    handelGetInquiryTemplate,
    handleRefetchInquiryTemplate,
    handleOpenGuideModal,
  } = useProductRegistHooks();

  const params = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { width, height, ref } = useResizeDetector();

  const [isOpenPreviewProductDetail, setIsOpenPreviewProductDetail] =
    useState<boolean>(false);

  const isMobile = width ? width < MAX_WIDTH : window.innerWidth < MAX_WIDTH;

  // 카테고리를 선택한 뒤부터 임시저장 허용. 카테고리 없이(standardCategoryId: "") 상품이 생성되는 것을 막는다.
  const isTemporarySaveAvailable =
    registPageNumber.basicPageNumber.page > 3 &&
    productParamInput.standardCategoryId !== '';

  const handleProductRequestInspection = () => {
    if (isRequestInspectionLoading) {
      return;
    }

    handleRequestInspection(productParamInput.id);
  };

  const handleCloseProductRegist = debounce(() => {
    HackleManager.logCreateCloseClick(
      isTemporarySaveAvailable ? '저장 후 나가기' : '나가기',
    );

    if (isTemporarySaveAvailable) {
      handleProductTemporarySave();

      return;
    }

    navigate(-1);
  }, 500);

  const handleClose = () => {
    HackleManager.logCreateCloseClick('나가기');

    navigate(-1);
  };

  const handleOpenGuide = () => {
    HackleManager.logCreateGuideClick(
      registPageNumber.page === 1
        ? '프립 만들기 시작 페이지'
        : registPageNumber.page === 2
        ? '판매 정보를 알려주세요'
        : '프립을 돋보이게 해주세요',
    );

    handleOpenGuideModal(true);
  };

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = '';

      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = ''; // 컴포넌트가 언마운트될 때 스크롤 복구
    };
  }, [isMobile]);

  const renderWebHeader = (): JSX.Element => {
    if (!isMobile) {
      return (
        <Row
          justifyContent="center"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 15,
            padding: '16px 0',
            width: '100%',
            background: '#414B56',
          }}
        >
          <Row
            justifyContent="flex-end"
            style={{
              width: '100%',
              maxWidth: '1200px',
            }}
          >
            <Row gap="6px">
              <Text
                color="#fff"
                size="16px"
                weight={600}
                onClick={handleCloseProductRegist}
                style={{ cursor: 'pointer' }}
              >
                {isTemporarySaveAvailable ? '저장 후 나가기' : '나가기'}
              </Text>
              <CommonImage
                src={ICON_CLOSE_WHITE}
                onClick={handleCloseProductRegist}
              />
            </Row>
          </Row>
        </Row>
      );
    }

    if (isShowCompleteView) {
      return (
        <Row
          justifyContent="space-between"
          style={{
            position: 'fixed',
            top: 0,
            padding: '0 20px',
            width: '100%',
            minHeight: '80px',
            maxHeight: '80px',
            background: '#fff',
          }}
        >
          <HeaderButton text="나가기" onClick={handleClose} />
        </Row>
      );
    }

    if (isTemporarySaveAvailable) {
      return (
        <Row
          gap="12px"
          justifyContent="flex-start"
          style={{
            position: 'sticky',
            top: 0,
            padding: '0 20px',
            width: '100%',
            minHeight: '80px',
            maxHeight: '80px',
            background: '#fff',
            zIndex: 1,
          }}
        >
          <HeaderButton
            text="저장 후 나가기 "
            onClick={handleCloseProductRegist}
          />
          {registPageNumber.page === 3 &&
            registPageNumber.informationPageNumber.page !== 0 && (
              <a href={PRODUCT_CREATE_GUIDE} target="_blank">
                <HeaderButton
                  text="가이드보기"
                  icon={<CommonImage src={ICON_PRODUCT_GUIDE} />}
                  onClick={() => {
                    HackleManager.logCreateGuideClick(
                      '프립을 돋보이게 해주세요',
                    );
                  }}
                />
              </a>
            )}
          {((registPageNumber.page === 2 &&
            registPageNumber.optionPageNumber.page !== 0) ||
            registPageNumber.page === 1 ||
            registPageNumber.page !== 3) && (
            <HeaderButton
              text="가이드보기"
              icon={<CommonImage src={ICON_PRODUCT_GUIDE} />}
              onClick={handleOpenGuide}
            />
          )}
          {registPageNumber.page === 3 && (
            <HeaderButton
              text="미리보기"
              icon={<CommonImage src={ICON_PRODUCT_PREVIEW} />}
              onClick={() => {
                HackleManager.logCreatePreviewClick(
                  params.id !== 'draft' ? Number(params.id) : undefined,
                );
                setIsOpenPreviewProductDetail(true);
              }}
            />
          )}
        </Row>
      );
    }

    return (
      <Row
        justifyContent="flex-start"
        gap="12px"
        style={{
          position: 'sticky',
          top: 0,
          padding: '0 20px',
          width: '100%',
          height: '100%',
          minHeight: '80px',
          maxHeight: '80px',
          background: '#fff',
        }}
      >
        <HeaderButton text="나가기" onClick={handleClose} />
        {registPageNumber.basicPageNumber.page !== 0 && (
          <HeaderButton
            text="가이드보기"
            icon={<CommonImage src={ICON_PRODUCT_GUIDE} />}
            onClick={handleOpenGuide}
          />
        )}
      </Row>
    );
  };

  const renderRegistPage = (): JSX.Element => {
    switch (registPageNumber.page) {
      case 1: {
        return (
          <BasicInformationRegist
            isMobile={isMobile}
            height={height}
            productParamInput={productParamInput}
            registPageNumber={registPageNumber}
            imageThumbnail={imageThumbnail}
            isOpenGuideModal={isOpenGuideModal}
            setProductParamInput={setProductParamInput}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            handleOpenGuideModal={handleOpenGuideModal}
          />
        );
      }
      case 2: {
        return (
          <OptionSelectRegist
            isMobile={isMobile}
            height={height}
            productParamInput={productParamInput}
            registPageNumber={registPageNumber}
            imageThumbnail={imageThumbnail}
            isOpenGuideModal={isOpenGuideModal}
            setProductParamInput={setProductParamInput}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            handleOpenGuideModal={handleOpenGuideModal}
          />
        );
      }
      case 3: {
        return (
          <InformationRegist
            isMobile={isMobile}
            height={height}
            productParamInput={productParamInput}
            registPageNumber={registPageNumber}
            imageThumbnail={imageThumbnail}
            inquiryList={inquiryList}
            setProductParamInput={setProductParamInput}
            setImageThumbnail={setImageThumbnail}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            handelGetInquiryTemplate={handelGetInquiryTemplate}
            handleRefetchInquiryTemplate={handleRefetchInquiryTemplate}
          />
        );
      }
      default: {
        return <></>;
      }
    }
  };

  return (
    <Container ref={ref}>
      <Helmet>
        <script src="https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js" />
        <script
          src={`//dapi.kakao.com/v2/maps/sdk.js?appkey=${getEnvConstant(
            'kakaoAppKey',
          )}&autoload=false&libraries=services`}
        />
        <script
          src={`https://maps.googleapis.com/maps/api/js?language=ko&key=${getEnvConstant(
            'googleMapApiKey',
          )}`}
        />
      </Helmet>
      {renderWebHeader()}
      {isShowCompleteView ? (
        <React.Fragment>
          <RegistCompleteContainer
            isMobile={isMobile}
            setIsShowCompleteView={setIsShowCompleteView}
            isRequestInspectionLoading={isRequestInspectionLoading}
            handleProductRequestInspection={handleProductRequestInspection}
          />
          {isRequestInspectionLoading && (
            <Loading style={{ background: 'rgba(0, 0, 0, 0.4)' }} />
          )}
          {isOpenModal.success && <InspectionComepleteModal />}
          {isOpenModal.failed && (
            <InspectionFaildeModal
              productParamInput={productParamInput}
              errorMessage={errorMessage}
            />
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {renderRegistPage()}
          <FloatingContainer
            isMobile={isMobile}
            registPageNumber={registPageNumber}
            setRegistPageNumber={setRegistPageNumber}
            isVaildCheckBasicPage={isVaildCheckBasicPage}
            handleNavigateBackwardPage={handleNavigateBackwardPage}
            handleNavigatePage={handleNavigatePage}
            isSubmitting={isSubmittingProduct}
          />
          {isSubmittingProduct && (
            <Loading style={{ background: 'rgba(0, 0, 0, 0.4)' }} />
          )}
        </React.Fragment>
      )}
      {isOpenPreviewProductDetail && (
        <ModalContainer
          isMobile={isMobile}
          isAbled={true}
          style={{ maxWidth: isMobile ? '100%' : '375px', gap: '0' }}
          onClick={() => {}}
          isBottomSheet={true}
          onClose={() => setIsOpenPreviewProductDetail(false)}
        >
          {isMobile && (
            <Column>
              <Row
                justifyContent="space-between"
                style={{
                  position: 'sticky',
                  top: 0,
                  padding: '24px 20px',
                  width: '100%',
                  background: '#fff',
                }}
              >
                <Text color="#1b1c1e" size="20px" weight={600}>
                  미리보기
                </Text>
                <Row onClick={() => setIsOpenPreviewProductDetail(false)}>
                  <IconClose color="#70737C" width="24px" height="24px" />
                </Row>
              </Row>
              <Row
                style={{
                  padding: '12px 0',
                  width: '100%',
                  background: 'rgba(27, 28, 30, 0.80)',
                }}
              >
                <Text color="#fff">
                  사용자에게 보여지는 화면을 미리 확인해 보세요.
                </Text>
              </Row>
            </Column>
          )}
          <PreviewProductDetail
            isMobile={isMobile}
            productParamInput={productParamInput}
            imageThumbnail={imageThumbnail}
          />
        </ModalContainer>
      )}
    </Container>
  );
};

export default ProductRegistPage;
